import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MapPin, Navigation, Phone, Clock, Building2, Filter, 
  X, ChevronDown, ChevronUp, Loader2, AlertCircle, List, Map, ArrowLeft,
  Mail, Globe, MessageCircle, Users, FileText, Info
} from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import axios from 'axios';
import RecurseroMapa from '../components/RecurseroMapa';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lista de tipos de institución
const TIPOS_INSTITUCION = [
  'Salud',
  'Educación', 
  'Desarrollo Social',
  'Justicia',
  'Seguridad',
  'ONG',
  'Otro'
];

export default function RecurseroMapaPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  // Estados
  const [instituciones, setInstituciones] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modo de visualización: 'cercanas' requiere ubicación, 'todas' no
  const [modoVisualizacion, setModoVisualizacion] = useState('todas'); // 'cercanas' o 'todas'
  
  // Ubicación del usuario
  const [userLocation, setUserLocation] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    radioKm: 5,
    tipoInstitucion: '',
    etiquetasSeleccionadas: []
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Vista
  const [vistaActiva, setVistaActiva] = useState('mapa'); // 'mapa' o 'lista'
  const [institucionSeleccionada, setInstitucionSeleccionada] = useState(null);
  const [showModalDetalle, setShowModalDetalle] = useState(false);
  const [institucionACentrar, setInstitucionACentrar] = useState(null);

  // Función para abrir modal con detalle completo y centrar el mapa
  const abrirModalDetalle = (inst, centrarMapa = false) => {
    setInstitucionSeleccionada(inst);
    setShowModalDetalle(true);
    if (centrarMapa && inst.coordenadas) {
      setInstitucionACentrar(inst);
    }
  };
  
  // Función para cuando se hace clic en la lista (abre modal Y centra)
  const handleClickEnLista = (inst) => {
    abrirModalDetalle(inst, true);
  };

  // Verificación de autenticación
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Cargar etiquetas al montar
  useEffect(() => {
    cargarEtiquetas();
  }, []);

  // Cargar todas las instituciones al montar o cuando cambia el modo
  useEffect(() => {
    if (modoVisualizacion === 'todas') {
      cargarTodasInstituciones();
    }
  }, [modoVisualizacion]);

  // Buscar instituciones cercanas cuando cambian los filtros o la ubicación
  useEffect(() => {
    if (modoVisualizacion === 'cercanas' && userLocation) {
      buscarInstituciones();
    }
  }, [userLocation, filtros, modoVisualizacion]);

  const cargarEtiquetas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/recursero-geo/etiquetas`);
      setEtiquetas(response.data);
    } catch (err) {
      console.error('Error cargando etiquetas:', err);
    }
  };

  // Cargar todas las instituciones con coordenadas
  const cargarTodasInstituciones = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_URL}/api/recursero-geo/instituciones-con-coordenadas`);
      // Aplicar filtros localmente si están activos
      let resultado = response.data;
      
      if (filtros.tipoInstitucion) {
        resultado = resultado.filter(inst => inst.tipo_institucion === filtros.tipoInstitucion);
      }
      
      if (filtros.etiquetasSeleccionadas.length > 0) {
        resultado = resultado.filter(inst => 
          inst.etiquetas?.some(et => filtros.etiquetasSeleccionadas.includes(et))
        );
      }
      
      setInstituciones(resultado);
    } catch (err) {
      setError('Error al cargar instituciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const obtenerUbicacion = useCallback(() => {
    setLoadingLocation(true);
    setGeoError('');
    
    if (!navigator.geolocation) {
      setGeoError('Tu navegador no soporta geolocalización');
      setLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setModoVisualizacion('cercanas'); // Cambiar a modo cercanas cuando se obtiene ubicación
        setLoadingLocation(false);
      },
      (error) => {
        let mensaje = 'Error al obtener ubicación';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            mensaje = 'Permiso de ubicación denegado. Habilitalo en la configuración del navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            mensaje = 'Ubicación no disponible';
            break;
          case error.TIMEOUT:
            mensaje = 'Tiempo de espera agotado';
            break;
          default:
            break;
        }
        setGeoError(mensaje);
        setLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, []);

  const buscarInstituciones = async () => {
    if (!userLocation) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/recursero-geo/buscar-cercanas`, {
        lat: userLocation.lat,
        lng: userLocation.lng,
        radio_km: filtros.radioKm,
        tipo_institucion: filtros.tipoInstitucion || null,
        etiquetas: filtros.etiquetasSeleccionadas.length > 0 ? filtros.etiquetasSeleccionadas : null,
        limite: 100
      });
      setInstituciones(response.data);
    } catch (err) {
      setError('Error al buscar instituciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleEtiqueta = (etiquetaId) => {
    setFiltros(prev => ({
      ...prev,
      etiquetasSeleccionadas: prev.etiquetasSeleccionadas.includes(etiquetaId)
        ? prev.etiquetasSeleccionadas.filter(id => id !== etiquetaId)
        : [...prev.etiquetasSeleccionadas, etiquetaId]
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      radioKm: 5,
      tipoInstitucion: '',
      etiquetasSeleccionadas: []
    });
  };

  const formatearDistancia = (km) => {
    if (km === undefined || km === null) return '';
    if (km < 1) {
      return `${Math.round(km * 1000)} m`;
    }
    return `${km.toFixed(1)} km`;
  };

  // Loading mientras verifica auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header del usuario */}
      <UserHeader />

      {/* Subtítulo de sección */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <MapPin className="w-10 h-10 text-orange-500" />
            <h1 className="text-3xl font-bold text-gray-900">Recursero en Mapa</h1>
          </div>
          <p className="text-gray-500">
            {modoVisualizacion === 'todas' 
              ? 'Explorá todas las instituciones del recursero colaborativo'
              : 'Encontrá instituciones y recursos cerca de tu ubicación actual'
            }
          </p>
        </div>
      </div>

      {/* Barra de modos, ubicación y filtros */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Toggle de modo: Todas / Cercanas */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setModoVisualizacion('todas')}
                className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                  modoVisualizacion === 'todas' 
                    ? 'bg-white shadow text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="btn-ver-todas"
              >
                <Building2 className="h-4 w-4" />
                Ver todas
              </button>
              <button
                onClick={() => {
                  if (userLocation) {
                    setModoVisualizacion('cercanas');
                  } else {
                    obtenerUbicacion();
                  }
                }}
                disabled={loadingLocation}
                className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                  modoVisualizacion === 'cercanas' 
                    ? 'bg-white shadow text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="btn-ver-cercanas"
              >
                {loadingLocation ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Navigation className="h-4 w-4" />
                )}
                Cercanas a mí
              </button>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Info de ubicación si está en modo cercanas */}
              {modoVisualizacion === 'cercanas' && userLocation && (
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-green-500" />
                  Ubicación detectada
                </span>
              )}
              
              {/* Toggle de vista mapa/lista */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setVistaActiva('mapa')}
                  className={`px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors ${
                    vistaActiva === 'mapa' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
                  }`}
                >
                  <Map className="h-4 w-4" /> Mapa
                </button>
                <button
                  onClick={() => setVistaActiva('lista')}
                  className={`px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors ${
                    vistaActiva === 'lista' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
                  }`}
                >
                  <List className="h-4 w-4" /> Lista
                </button>
              </div>

              {/* Botón de filtros */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-1"
              >
                <Filter className="h-4 w-4" />
                Filtros
                {(filtros.tipoInstitucion || filtros.etiquetasSeleccionadas.length > 0) && (
                  <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1.5 rounded-full">
                    {(filtros.tipoInstitucion ? 1 : 0) + filtros.etiquetasSeleccionadas.length}
                  </span>
                )}
                {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Error de geolocalización */}
          {geoError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {geoError}
            </div>
          )}
        </div>

        {/* Panel de filtros */}
        {showFilters && userLocation && (
          <div className="border-t bg-gray-50 px-4 py-4">
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-3 gap-4">
                {/* Radio de búsqueda */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Radio de búsqueda
                  </label>
                  <select
                    value={filtros.radioKm}
                    onChange={(e) => setFiltros(prev => ({ ...prev, radioKm: Number(e.target.value) }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value={1}>1 km</option>
                    <option value={2}>2 km</option>
                    <option value={5}>5 km</option>
                    <option value={10}>10 km</option>
                    <option value={20}>20 km</option>
                  </select>
                </div>

                {/* Tipo de institución */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de institución
                  </label>
                  <select
                    value={filtros.tipoInstitucion}
                    onChange={(e) => setFiltros(prev => ({ ...prev, tipoInstitucion: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Todos los tipos</option>
                    {TIPOS_INSTITUCION.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>

                {/* Botón limpiar */}
                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
                    <X className="h-4 w-4 mr-1" /> Limpiar filtros
                  </Button>
                </div>
              </div>

              {/* Etiquetas */}
              {etiquetas.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Características
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {etiquetas.map(etq => (
                      <button
                        key={etq.id}
                        onClick={() => toggleEtiqueta(etq.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          filtros.etiquetasSeleccionadas.includes(etq.id)
                            ? 'text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={filtros.etiquetasSeleccionadas.includes(etq.id) ? { backgroundColor: etq.color } : {}}
                      >
                        {etq.nombre}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {modoVisualizacion === 'cercanas' && !userLocation ? (
          /* Estado inicial - sin ubicación en modo cercanas */
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center">
              <Navigation className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                Compartí tu ubicación
              </h2>
              <p className="text-gray-500 mb-4">
                Para encontrar instituciones cercanas, necesitamos acceder a tu ubicación actual.
              </p>
              <Button onClick={obtenerUbicacion} disabled={loadingLocation} className="gap-2">
                {loadingLocation ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
                Activar ubicación
              </Button>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">¿No querés compartir tu ubicación?</p>
                <Button variant="outline" onClick={() => setModoVisualizacion('todas')} className="gap-2">
                  <Building2 className="h-4 w-4" />
                  Ver todas las instituciones
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Mostrar mapa y lista */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Mapa */}
            <div className={`lg:col-span-2 ${vistaActiva === 'lista' ? 'hidden lg:block' : ''}`}>
              <RecurseroMapa
                instituciones={instituciones}
                userLocation={modoVisualizacion === 'cercanas' ? userLocation : null}
                radioKm={modoVisualizacion === 'cercanas' ? filtros.radioKm : null}
                height="calc(100vh - 250px)"
                onInstitucionClick={abrirModalDetalle}
                centroInicial={modoVisualizacion === 'todas' ? { lat: -34.75, lng: -58.40 } : null}
                zoomInicial={modoVisualizacion === 'todas' ? 12 : null}
                centrarEnInstitucion={institucionACentrar}
              />
            </div>

            {/* Lista de resultados */}
            <div className={`${vistaActiva === 'mapa' ? 'hidden lg:block' : ''}`}>
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b">
                  <h2 className="font-semibold text-gray-900">
                    {loading ? 'Buscando...' : `${instituciones.length} instituciones encontradas`}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {modoVisualizacion === 'cercanas' 
                      ? `En un radio de ${filtros.radioKm} km` 
                      : 'Mostrando todas las instituciones con ubicación'
                    }
                  </p>
                </div>

                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                  </div>
                ) : instituciones.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p>No se encontraron instituciones</p>
                    {modoVisualizacion === 'cercanas' && (
                      <p className="text-sm">Probá ampliar el radio de búsqueda</p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y max-h-[calc(100vh-350px)] overflow-y-auto">
                    {instituciones.map(inst => (
                      <div 
                        key={inst.id}
                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                          institucionSeleccionada?.id === inst.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        onClick={() => handleClickEnLista(inst)}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="font-medium text-gray-900 text-sm">
                            {inst.nombre_organismo || inst.nombre}
                          </h3>
                          {modoVisualizacion === 'cercanas' && inst.distancia_km !== undefined && (
                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                              {formatearDistancia(inst.distancia_km)}
                            </span>
                          )}
                        </div>
                        
                        {inst.area_desempeno && (
                          <p className="text-xs text-gray-600 mb-1">{inst.area_desempeno}</p>
                        )}
                        
                        <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
                          <MapPin className="h-3 w-3" />
                          {inst.municipio}
                          {inst.localidad && `, ${inst.localidad}`}
                        </p>

                        {/* Etiquetas */}
                        {inst.etiquetas_info && inst.etiquetas_info.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {inst.etiquetas_info.map(etq => (
                              <span 
                                key={etq.id}
                                className="text-xs px-1.5 py-0.5 rounded-full text-white"
                                style={{ backgroundColor: etq.color }}
                              >
                                {etq.nombre}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Info de contacto */}
                        {(inst.telefonos?.length > 0 || inst.horarios) && (
                          <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-3 text-xs text-gray-500">
                            {inst.telefonos?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {inst.telefonos[0]}
                              </span>
                            )}
                            {inst.horarios && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {inst.horarios.substring(0, 30)}...
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de detalle completo de institución */}
      {showModalDetalle && institucionSeleccionada && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowModalDetalle(false)}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal - fijo */}
            <div className="flex-shrink-0 bg-white border-b px-6 py-4 flex items-start justify-between rounded-t-xl">
              <div className="flex-1 pr-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {institucionSeleccionada.nombre_organismo || institucionSeleccionada.nombre}
                </h2>
                {institucionSeleccionada.area_desempeno && (
                  <p className="text-sm text-gray-600 mt-1">{institucionSeleccionada.area_desempeno}</p>
                )}
              </div>
              <button 
                onClick={() => setShowModalDetalle(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Contenido del modal - scrolleable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Etiquetas */}
              {institucionSeleccionada.etiquetas_info?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {institucionSeleccionada.etiquetas_info.map(etq => (
                    <span 
                      key={etq.id}
                      className="text-sm px-3 py-1 rounded-full text-white font-medium"
                      style={{ backgroundColor: etq.color }}
                    >
                      {etq.nombre}
                    </span>
                  ))}
                </div>
              )}

              {/* Ubicación */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-500" />
                  Ubicación
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Municipio:</span> {institucionSeleccionada.municipio}
                  </p>
                  {institucionSeleccionada.localidad && (
                    <p className="text-gray-700">
                      <span className="font-medium">Localidad:</span> {institucionSeleccionada.localidad}
                    </p>
                  )}
                  {institucionSeleccionada.domicilio && (
                    <p className="text-gray-700">
                      <span className="font-medium">Dirección:</span> {
                        typeof institucionSeleccionada.domicilio === 'string' 
                          ? institucionSeleccionada.domicilio 
                          : `${institucionSeleccionada.domicilio.calle || ''} ${institucionSeleccionada.domicilio.numero || ''}`
                      }
                    </p>
                  )}
                  {institucionSeleccionada.direccion_texto && (
                    <p className="text-gray-700">
                      <span className="font-medium">Dirección:</span> {institucionSeleccionada.direccion_texto}
                    </p>
                  )}
                  {modoVisualizacion === 'cercanas' && institucionSeleccionada.distancia_km !== undefined && (
                    <p className="text-blue-600 font-medium">
                      📍 A {formatearDistancia(institucionSeleccionada.distancia_km)} de tu ubicación
                    </p>
                  )}
                </div>
              </div>

              {/* Contacto */}
              {(institucionSeleccionada.telefonos?.length > 0 || institucionSeleccionada.emails?.length > 0 || institucionSeleccionada.web || institucionSeleccionada.whatsapp) && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-blue-500" />
                    Contacto
                  </h3>
                  <div className="space-y-3">
                    {institucionSeleccionada.telefonos?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {institucionSeleccionada.telefonos.map((tel, idx) => (
                          <a 
                            key={idx}
                            href={`tel:${tel}`}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-blue-600 hover:bg-blue-100 transition-colors text-sm font-medium"
                          >
                            <Phone className="h-4 w-4" />
                            {tel}
                          </a>
                        ))}
                      </div>
                    )}
                    {institucionSeleccionada.emails?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {institucionSeleccionada.emails.map((email, idx) => (
                          <a 
                            key={idx}
                            href={`mailto:${email}`}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-blue-600 hover:bg-blue-100 transition-colors text-sm"
                          >
                            <Mail className="h-4 w-4" />
                            {email}
                          </a>
                        ))}
                      </div>
                    )}
                    {institucionSeleccionada.whatsapp && (
                      <a 
                        href={`https://wa.me/${institucionSeleccionada.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                      >
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp: {institucionSeleccionada.whatsapp}
                      </a>
                    )}
                    {institucionSeleccionada.web && (
                      <a 
                        href={institucionSeleccionada.web.startsWith('http') ? institucionSeleccionada.web : `https://${institucionSeleccionada.web}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-blue-600 hover:bg-blue-100 transition-colors text-sm"
                      >
                        <Globe className="h-4 w-4" />
                        {institucionSeleccionada.web}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Horarios */}
              {institucionSeleccionada.horarios && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    Horarios de atención
                  </h3>
                  <p className="text-gray-700 text-sm whitespace-pre-line">{institucionSeleccionada.horarios}</p>
                </div>
              )}

              {/* Demandas que atiende */}
              {institucionSeleccionada.demandas_atiende && (
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-600" />
                    Demandas que atiende
                  </h3>
                  <p className="text-gray-700 text-sm whitespace-pre-line">{institucionSeleccionada.demandas_atiende}</p>
                </div>
              )}

              {/* Requisitos de acceso */}
              {institucionSeleccionada.requisitos_acceso && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    Requisitos de acceso
                  </h3>
                  <p className="text-gray-700 text-sm whitespace-pre-line">{institucionSeleccionada.requisitos_acceso}</p>
                </div>
              )}

              {/* Indicaciones / Comentarios */}
              {institucionSeleccionada.indicaciones && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Info className="h-5 w-5 text-gray-600" />
                    Indicaciones adicionales
                  </h3>
                  <p className="text-gray-700 text-sm whitespace-pre-line">{institucionSeleccionada.indicaciones}</p>
                </div>
              )}

              {/* Referentes */}
              {institucionSeleccionada.referentes?.length > 0 && institucionSeleccionada.referentes.some(r => r.nombre && r.vigente !== false) && (
                <div className="bg-indigo-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    Referentes
                  </h3>
                  <div className="space-y-2">
                    {institucionSeleccionada.referentes
                      .filter(r => r.nombre && r.vigente !== false)
                      .map((ref, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-medium text-gray-900">{ref.nombre}</span>
                          {ref.cargo && <span className="text-gray-600"> - {ref.cargo}</span>}
                          {ref.contacto && (
                            <span className="text-blue-600 ml-2">{ref.contacto}</span>
                          )}
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Áreas */}
              {institucionSeleccionada.areas?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Áreas de trabajo</h3>
                  <div className="flex flex-wrap gap-2">
                    {institucionSeleccionada.areas.map((area, idx) => (
                      <span 
                        key={idx}
                        className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-full"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer del modal - fijo */}
            <div className="flex-shrink-0 bg-gray-50 border-t px-6 py-4 rounded-b-xl">
              <Button 
                onClick={() => setShowModalDetalle(false)}
                className="w-full"
                variant="outline"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Botón volver */}
      <div className="text-center py-8 bg-gray-50">
        <Button 
          variant="outline" 
          onClick={() => navigate('/recursero')}
          data-testid="back-to-recursero-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Recursero
        </Button>
      </div>
    </div>
  );
}
